// Astra's input box for the free read (founder pick A v2, 2026-09-30:
// "typing animation and mic button"), ported from the mock onto the real flow.
//
//   * A typing loop cycles the four shipped sample students (TryHero's SEEDS,
//     word for word, the engineering junior first). It is drawn in an
//     aria-hidden overlay, never the placeholder: the textarea's accessible
//     name is one static example, so a screen reader hears a label, not a loop.
//   * The loop stops the moment the box is focused, holds text, or the mic is
//     listening; prefers-reduced-motion shows the first sample, still.
//   * "Try this one" runs the sample on screen.
//   * The mic uses the browser's own SpeechRecognition. The transcript lands
//     in the box to read and edit before sending; no audio ever reaches us
//     (the browser's speech service handles it). Hidden when the browser has
//     none, with a pointer to the keyboard's own mic.
//   * Interim results show live and are rebuilt on every event, so the text
//     corrects itself while the visitor is still speaking (founder,
//     2026-09-30: "correct dictation, grammar and logical errors").
//   * Domain phrase hints (GPA, AP, IB, SAT, UC names...) go with the session
//     only where they can work: Chrome applies contextual biasing to
//     ON-DEVICE recognition, so the hints are set only when the browser says
//     on-device English is already installed ("available"). We never ask the
//     visitor's browser to download a speech model; everywhere else the mic
//     is unchanged.
import React, { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon, ArrowRightIcon, MicrophoneIcon, StopIcon } from '@heroicons/react/24/outline';
import { SEEDS } from '../TryHero';
import { COMPOSER, DICTATION_PHRASES } from '../../../content/astraLite';
import { track, EVENTS } from '../../../services/analytics';

const ORDER = ['engineering', 'detailed', 'business', 'undecided'];
export const SAMPLES = ORDER.map((k) => SEEDS.find((s) => s.key === k));
const TYPE_MS = 28;
const HOLD_MS = 2600;
// The picture's floor (#5237): a GPA plus a score or an activity ("3.9, 1450,
// Robotics captain", 27) is what people type from a chances ad; the old 40
// left the button dead. The route's astra_lite.MIN_TEXT is this number,
// pinned by test_astra_lite.py.
export const MIN_CHARS = 12;

function usePrefersReducedMotion() {
    // Read after mount, not in the initializer: the prerender cannot know the
    // visitor's setting, and the first render must match it (#2605).
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mq) return undefined;
        setReduced(mq.matches);
        const on = () => setReduced(mq.matches);
        mq.addEventListener?.('change', on);
        return () => mq.removeEventListener?.('change', on);
    }, []);
    return reduced;
}

function useTypingLoop(active, samples = SAMPLES) {
    const [idx, setIdx] = useState(0);
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!active) return undefined;
        const full = samples[idx].text.length;
        const t = n < full
            ? setTimeout(() => setN(n + 1), TYPE_MS)
            : setTimeout(() => { setN(0); setIdx((idx + 1) % samples.length); }, HOLD_MS);
        return () => clearTimeout(t);
    }, [active, idx, n, samples]);
    return { idx, text: samples[idx].text.slice(0, n), done: n >= samples[idx].text.length };
}

// How long the first mic tap waits on the on-device check before starting
// the cloud mic anyway.
export const ON_DEVICE_WAIT_MS = 1500;

const recognitionCtor = () => (typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;

// Chrome's built-in Proofreader, on-device: offered only when the model is
// already on the machine ("available"), so it never downloads anything and
// no text leaves the browser. Everywhere else the button does not exist.
export async function proofreaderReady() {
    try {
        const P = typeof window !== 'undefined' ? window.Proofreader : undefined;
        if (!P?.availability || !P?.create) return false;
        return (await P.availability({ expectedInputLanguages: ['en'] })) === 'available';
    } catch {
        return false;
    }
}

export async function proofread(text) {
    const p = await window.Proofreader.create({ expectedInputLanguages: ['en'] });
    try {
        const r = await p.proofread(text);
        return typeof r?.correctedInput === 'string' ? r.correctedInput : text;
    } finally {
        try { p.destroy?.(); } catch { /* gone */ }
    }
}

// Whether on-device English recognition is already installed: the only case
// in which Chrome applies phrase hints. Never installs anything.
export async function onDeviceReady(Recognition) {
    try {
        if (!Recognition?.available || typeof window === 'undefined' || !window.SpeechRecognitionPhrase) return false;
        return (await Recognition.available({ langs: ['en-US'], processLocally: true })) === 'available';
    } catch {
        return false;
    }
}

// Contextual biasing on an on-device session. Anything unexpected (a throw,
// no `phrases`) means no hints and the session runs as before.
export function phraseHints(rec) {
    const Phrase = typeof window !== 'undefined' ? window.SpeechRecognitionPhrase : undefined;
    if (!Phrase || !rec || !('phrases' in rec)) return false;
    try {
        rec.processLocally = true;
        rec.phrases = DICTATION_PHRASES.map((p) => new Phrase(p, 3.0));
        return true;
    } catch {
        try { rec.processLocally = false; rec.phrases = []; } catch { /* leave it */ }
        return false;
    }
}

// `question` (#2725, the open chat): the same box asks a question instead of
// building a picture: two rows, Enter sends (Shift+Enter is a new line), the
// box clears once sent, 500 characters at most. The samples, the button word,
// the label and the line under it come from the caller. Every default is the
// picture box's, unchanged.
// `restore` ({text, n}): a question that did not go through comes back into
// the box (the open chat; a new `n` each time).
// `secondary` ({label, onClick, cta, testid}): an outline button just left of
// the primary one (#5237, the chat's "Sign in instead"); none by default.
export default function AstraComposer({ onRun, busy = false, samples = SAMPLES, goLabel = COMPOSER.go,
    minChars = MIN_CHARS, ariaLabel = COMPOSER.ariaLabel, savedLine = COMPOSER.saved, question = false,
    restore = null, secondary = null }) {
    const compact = question;
    const reduced = usePrefersReducedMotion();
    const [value, setValue] = useState('');
    const [focused, setFocused] = useState(false);
    const [mic, setMic] = useState('idle');   // idle | listening | blocked
    const recRef = useRef(null);
    // Found after mount, never during render: the prerendered page (#2567)
    // is built where no browser speech exists, so reading it in render made
    // the client's first pass differ from the HTML (React #418, #3812).
    const [Recognition, setRecognition] = useState(null);
    useEffect(() => { setRecognition(() => recognitionCtor()); }, []);
    // Whether on-device English is installed: null until the first mic tap
    // asks. Never asked on mount: chrome-headless-shell has no on-device
    // speech service and kills the page's renderer the moment
    // SpeechRecognition.available() runs (#3812), and a visitor who never
    // taps the mic has no use for the answer.
    const localRef = useRef(null);
    const probingRef = useRef(false);
    const [canTidy, setCanTidy] = useState(false);
    const [tidy, setTidy] = useState(null);      // null | 'working' | {text} | 'none'
    useEffect(() => {
        let alive = true;
        proofreaderReady().then((ok) => { if (alive) setCanTidy(ok); });
        return () => { alive = false; };
    }, []);
    useEffect(() => {
        if (restore && typeof restore.text === 'string') setValue(restore.text);
    }, [restore]);
    const valueRef = useRef('');
    const boxRef = useRef(null);
    useEffect(() => { valueRef.current = value; }, [value]);
    const runRef = useRef(0);          // bumped by a submit: a check in flight is void
    const runTidy = async () => {
        const original = value;
        const run = ++runRef.current;
        setTidy('working');
        let timer;
        try {
            // A model that never answers must not leave the button stuck.
            const fixed = await Promise.race([proofread(original),
                new Promise((_, no) => { timer = setTimeout(() => no(new Error('timeout')), 10000); })]);
            // The visitor typed, dictated or sent since the click: this result
            // is for text that is no longer there (review 2026-09-30).
            if (run !== runRef.current || valueRef.current !== original) { setTidy(null); return; }
            const changed = fixed.trim() && fixed.trim() !== original.trim();
            setTidy(changed ? { text: fixed, for: original } : 'none');
            track(EVENTS.ASTRA_LITE_TIDY, { state: changed ? 'offered' : 'none' });
        } catch {
            setTidy(null);
        } finally {
            clearTimeout(timer);
        }
    };
    const submitting = () => { runRef.current += 1; setTidy(null); };
    const send = (text, key) => {
        submitting();
        onRun?.(text, key);
        if (question) setValue('');
    };
    // Any change to the text (typing, dictation, a sample) retires an offer.
    useEffect(() => {
        setTidy((t) => (t && typeof t === 'object' && t.for !== value ? null : t === 'none' ? null : t));
    }, [value]);

    const showDemo = !value && !focused && mic !== 'listening';
    const loop = useTypingLoop(showDemo && !reduced && !busy, samples);
    const demoIdx = reduced ? 0 : loop.idx;
    const demoText = reduced ? samples[0].text : loop.text;

    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            try { recRef.current?.stop(); } catch { /* already stopped */ }
        };
    }, []);

    const tryThis = () => {
        const seed = samples[demoIdx];
        if (!question) setValue(seed.text);
        send(seed.text, seed.key);
    };

    const toggleMic = () => {
        if (mic === 'listening') { try { recRef.current?.stop(); } catch { /* stopped */ } return; }
        if (probingRef.current) return;
        // The one on-device question, on the first tap only, and only where
        // phrase hints could apply. Capped so a slow answer never holds the
        // mic: past the cap the session runs as the cloud mic.
        if (localRef.current === null && Recognition?.available && window.SpeechRecognitionPhrase) {
            probingRef.current = true;
            let timer;
            Promise.race([onDeviceReady(Recognition),
                new Promise((ok) => { timer = setTimeout(() => ok(false), ON_DEVICE_WAIT_MS); })])
                .then((ok) => { localRef.current = ok === true; })
                .finally(() => { clearTimeout(timer); probingRef.current = false; if (mountedRef.current) startMic(); });
            return;
        }
        startMic();
    };

    const startMic = () => {
        let rec;
        try { rec = new Recognition(); } catch { setMic('blocked'); return; }
        rec.lang = 'en-US';
        rec.interimResults = true;
        rec.continuous = true;
        if (localRef.current) phraseHints(rec);
        const now = valueRef.current;
        const base = now ? `${now.trim()} ` : '';
        rec.onresult = (e) => {
            let said = '';
            for (let i = 0; i < e.results.length; i += 1) said += e.results[i][0].transcript;
            setValue((base + said.trim()).slice(0, 2000));
        };
        rec.onerror = (e) => {
            const blocked = e?.error === 'not-allowed' || e?.error === 'service-not-allowed';
            setMic(blocked ? 'blocked' : 'idle');
            if (blocked) track(EVENTS.ASTRA_LITE_MIC, { state: 'blocked' });
        };
        rec.onend = () => setMic((m) => (m === 'blocked' ? m : 'idle'));
        recRef.current = rec;
        setMic('listening');
        track(EVENTS.ASTRA_LITE_MIC, { state: 'started' });
        try { rec.start(); } catch { setMic('blocked'); }
    };

    const typed = value.trim().length;
    const ready = typed >= minChars && !busy;
    // Text under the picture's floor says what to add, so the button is never
    // dead without a reason (#5237). The question box sends from 2 characters.
    const short = !question && typed > 0 && typed < minChars && !busy;

    return (
        <div className="pt-2">
            <div className={`relative rounded-2xl border bg-white shadow-sm ${mic === 'listening' ? 'border-[#1A4D2E] ring-2 ring-[#1A4D2E]/20' : 'border-[#E0DED8]'}`}>
                <textarea
                    data-testid="astra-composer"
                    value={value}
                    ref={boxRef}
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onKeyDown={(e) => {
                        const sends = question ? !e.shiftKey : (e.metaKey || e.ctrlKey);
                        if (e.key === 'Enter' && sends && (ready || question)) {
                            e.preventDefault();
                            if (ready) send(value, null);
                        }
                    }}
                    rows={compact ? 2 : 7}
                    maxLength={question ? 500 : 2000}
                    spellCheck
                    autoCorrect="on"
                    aria-label={ariaLabel}
                    disabled={busy}
                    className={`block w-full resize-none rounded-2xl bg-transparent p-4 text-[15px] leading-relaxed text-[#2C2C2C] focus:outline-none ${secondary ? 'pb-28 sm:pb-16' : 'pb-16'} ${compact ? 'h-[120px]' : 'h-72 sm:h-44'}`}
                />
                {showDemo && (
                    <div aria-hidden="true" data-testid="astra-demo-text"
                        className={`pointer-events-none absolute inset-x-4 top-4 flex flex-col justify-end overflow-hidden text-[15px] leading-relaxed text-[#6B6B6B] ${secondary ? 'bottom-28 sm:bottom-16' : 'bottom-16'}`}>
                        <p>
                            {demoText}
                            {!reduced && !loop.done && <span className="ml-0.5 inline-block h-[1.1em] w-[2px] translate-y-[3px] animate-pulse bg-[#1A4D2E]" />}
                        </p>
                    </div>
                )}
                <div className={`absolute inset-x-3 bottom-3 flex items-center justify-end gap-2 ${secondary ? 'flex-wrap' : ''}`}>
                    {canTidy && value.trim().length >= 10 && mic !== 'listening' && (
                        <button type="button" onClick={runTidy} data-testid="astra-tidy" disabled={busy || tidy === 'working'}
                            className="h-11 whitespace-nowrap rounded-xl border border-[#CFE0D2] bg-[#F4F8F3] px-3 text-sm font-semibold text-[#1A4D2E] hover:bg-[#E8F0E9] disabled:opacity-60">
                            {tidy === 'working' ? COMPOSER.tidying : COMPOSER.tidy}
                        </button>
                    )}
                    {Recognition && (
                        <button type="button" onClick={() => toggleMic()} data-testid="astra-mic" disabled={busy}
                            aria-label={mic === 'listening' ? COMPOSER.micStop : COMPOSER.micStart}
                            aria-pressed={mic === 'listening'}
                            className={`relative flex h-11 w-11 items-center justify-center rounded-full border transition-colors ${mic === 'listening'
                                ? 'border-[#C05838] bg-[#C05838] text-white'
                                : 'border-[#CFE0D2] bg-[#F4F8F3] text-[#1A4D2E] hover:bg-[#E8F0E9]'}`}>
                            {mic === 'listening' && <span className="absolute inset-0 animate-ping rounded-full bg-[#C05838]/40 motion-reduce:animate-none" aria-hidden="true" />}
                            {mic === 'listening' ? <StopIcon className="relative h-5 w-5" aria-hidden="true" /> : <MicrophoneIcon className="h-5 w-5" aria-hidden="true" />}
                        </button>
                    )}
                    {secondary && (
                        <button type="button" onClick={secondary.onClick} data-cta={secondary.cta || undefined}
                            data-testid={secondary.testid || 'astra-composer-secondary'} disabled={busy}
                            className="inline-flex h-11 items-center whitespace-nowrap rounded-xl border border-[#CFE0D2] bg-white px-4 text-sm font-semibold text-[#1A4D2E] hover:bg-[#F4F8F3] disabled:opacity-60">
                            {secondary.label}
                        </button>
                    )}
                    <button type="button" onClick={() => send(value, null)} disabled={!ready}
                        data-testid="astra-build"
                        className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-[#1A4D2E] px-4 text-sm font-semibold text-white hover:bg-[#2D6B45] disabled:opacity-60">
                        {busy
                            ? <><ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" /> {goLabel}</>
                            : <>{goLabel} <ArrowRightIcon className="h-4 w-4" aria-hidden="true" /></>}
                    </button>
                </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1">
                {mic === 'listening' ? (
                    <span data-testid="astra-mic-listening" aria-live="polite"
                        className="flex min-h-[44px] items-center gap-2 text-sm font-semibold text-[#C05838]">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-[#C05838] motion-reduce:animate-none" aria-hidden="true" />
                        {COMPOSER.listening}
                    </span>
                ) : showDemo && !busy ? (
                    <button type="button" onClick={tryThis} data-testid="astra-try-this"
                        className="min-h-[44px] text-sm font-semibold text-[#1A4D2E] underline underline-offset-4 hover:text-[#2D6B45]">
                        {COMPOSER.tryThis}
                    </button>
                ) : (
                    <span data-testid="astra-composer-hint" aria-live="polite" className="text-xs font-medium text-[#4A4A4A]">
                        {short ? COMPOSER.short : ''}
                    </span>
                )}
                <p className="text-xs text-[#6B6B6B]">{savedLine}</p>
            </div>
            {tidy && typeof tidy === 'object' && (
                <div data-testid="astra-tidy-offer" role="status" aria-live="polite" className="mt-3 rounded-xl border border-[#CFE0D2] bg-[#F4F8F3] p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#1A4D2E]">{COMPOSER.tidyHeading}</p>
                    <p data-testid="astra-tidy-text" className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-[#2C2C2C]">{tidy.text}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button type="button" data-testid="astra-tidy-use"
                            onClick={() => {
                                if (tidy.for !== valueRef.current) { setTidy(null); return; }
                                setValue(tidy.text.slice(0, 2000)); setTidy(null);
                                track(EVENTS.ASTRA_LITE_TIDY, { state: 'used' });
                                boxRef.current?.focus();
                            }}
                            className="min-h-[44px] rounded-lg bg-[#1A4D2E] px-4 text-sm font-semibold text-white hover:bg-[#2D6B45]">
                            {COMPOSER.tidyUse}
                        </button>
                        <button type="button" data-testid="astra-tidy-keep" onClick={() => { setTidy(null); track(EVENTS.ASTRA_LITE_TIDY, { state: 'kept' }); boxRef.current?.focus(); }}
                            className="min-h-[44px] rounded-lg px-3 text-sm font-semibold text-[#1A4D2E] underline underline-offset-4">
                            {COMPOSER.tidyKeep}
                        </button>
                        <span className="text-xs text-[#6B6B6B]">{COMPOSER.tidyNote}</span>
                    </div>
                </div>
            )}
            {tidy === 'none' && (
                <p data-testid="astra-tidy-none" role="status" aria-live="polite" className="mt-2 px-1 text-sm text-[#1A4D2E]">{COMPOSER.tidyNone}</p>
            )}
            {mic === 'blocked' && (
                <p role="alert" data-testid="astra-mic-blocked"
                    className="mt-2 rounded-lg border border-[#F0D5C8] bg-[#FCF7F4] px-3 py-2 text-sm text-[#7A3A22]">
                    {COMPOSER.blocked}
                </p>
            )}
            {!Recognition && <p data-testid="astra-mic-hint" className="mt-1 px-1 text-xs text-[#6B6B6B]">{COMPOSER.noMicHint}</p>}
        </div>
    );
}
